// Shared helper for pinning leading/trailing table columns (e.g. a leading
// "Type" column and a trailing "Actions" column) so they stay visually fixed
// while the middle, data-heavy columns scroll horizontally underneath them.
//
// Every property here is set via inline `style`, not Tailwind classes — that
// makes the sticky/left/right/z-index values immune to class-merge ordering
// and keeps header + body cells for the same column pinned at identical
// offsets. Any table can adopt this by building a config once and calling
// getStickyCellProps(columnId) for both its TableHead and TableCell cells.
//
// Example:
//   const sticky = createStickyColumns({
//     left: { select: 40, document_type: 60 },
//     right: ["actions"],
//   });
//   ...
//   <TableHead style={sticky.getStickyCellProps(header.column.id, true).style}>
//   <TableCell style={sticky.getStickyCellProps(cell.column.id).style}>
export const createStickyColumns = ({ left = {}, right = [] }) => {
  const leftOrder = Object.keys(left);

  // `isHeader` is true only for <TableHead> cells. Header cells always get
  // an explicit inline `position: sticky; top: 0` here — deliberately NOT
  // left to the shadcn Table's own "sticky top-0" Tailwind class — so a
  // pinned Type/Actions header is locked to the top-left/top-right corner
  // via inline style alone, immune to class-merge ordering. Body cells
  // never get `top` — only the header row should stick vertically.
  //
  // Header pinned cells use a HIGHER z-index (30) than body pinned cells
  // (20): both are `position: sticky` with an opaque white background, and
  // a scrolling body row's pinned cell sits later in the DOM than <thead>,
  // so on a z-index tie it would paint over — and visually blank out — the
  // sticky header as rows pass underneath it. The gap guarantees the
  // header always wins regardless of paint order. `overflow: hidden` is
  // deliberately NOT set here — combined with position: sticky on a
  // border-collapsed table it can trigger a Chromium/WebKit repaint bug
  // where the cell's own content fails to redraw once the cell is stuck.
  const getStickyCellProps = (columnId, isHeader = false) => {
    const zIndex = isHeader ? 30 : 20;
    const base = isHeader
      ? { position: "sticky", top: 0, zIndex, backgroundColor: "white" }
      : {};

    const leftIndex = leftOrder.indexOf(columnId);
    if (leftIndex !== -1) {
      const leftOffset = leftOrder
        .slice(0, leftIndex)
        .reduce((sum, id) => sum + (left[id] ?? 0), 0);
      const width = left[columnId];
      return {
        style: {
          ...base,
          position: "sticky",
          left: leftOffset,
          width,
          minWidth: width,
          maxWidth: width,
          zIndex,
          backgroundColor: "white",
        },
      };
    }
    if (right.includes(columnId)) {
      return {
        style: {
          ...base,
          position: "sticky",
          right: 0,
          zIndex,
          backgroundColor: "white",
        },
      };
    }
    return { style: base };
  };

  return { getStickyCellProps };
};
